import Choices from 'choices.js';
import { choices } from './helpers';

export type SectionAjaxData = {
  url: string;
  _wpnonce: string;
  action: string;
  post_type: string;
  listing_color: string;
  listing_type: string;
  listing_columns: string;
};
type ExtendedOptions = {
  value: string;
  name?: string;
  label: string;
  count?: number;
};
type ExtendedSelect = {
  primary: boolean;
  select: Choices;
  counts?: { [key: string]: ExtendedOptions[] };
  options: ExtendedOptions[];
};

export class AjaxForm {
  url: string;
  data: SectionAjaxData;
  form: HTMLFormElement;
  listing: HTMLElement;
  pagination?: HTMLElement;
  hasAdvancedFilters: boolean = false;
  filters: Map<string, ExtendedSelect> = new Map();
  filterSetup: { primary?: string; secondary?: string } = {};

  constructor(public section: HTMLElement) {
    this.section = section;
    this.data = section.dataset as SectionAjaxData;

    this.listing = section.querySelector('#listing') as HTMLElement;
    if (!this.listing) {
      console.warn('Listing missing!');
      return;
    }
    this.setupPagination();

    if (!this.data.url) {
      console.warn('Missing url!');
      return;
    }
    this.url = this.data.url;
    this.form = section.querySelector('#filter') as HTMLFormElement;
    if (!this.form) {
      console.warn('Form missing!');
      return;
    }
    this.setupFilter();
  }

  setupFilter() {
    // setup filters and figure out primary/secondary filters
    const filters = this.form.querySelectorAll(
      '.filter-select'
    ) as NodeListOf<HTMLSelectElement>;
    filters.forEach((filter: HTMLSelectElement) => {
      const data = filter.dataset;
      const options = Array.from(filter.options).map(
        (option: HTMLOptionElement) => ({
          value: option.value,
          name: option.dataset?.label,
          label: option.label,
          count: option.dataset?.count
            ? parseInt(option.dataset?.count)
            : undefined,
        })
      );
      const select: ExtendedSelect = {
        primary: data.primary === 'true',
        select: choices(filter),
        options,
      };
      this.filterSetup[data.primary === 'true' ? 'primary' : 'secondary'] =
        filter.name;

      if (filter.dataset?.counts) {
        this.hasAdvancedFilters = true;
        select.counts = JSON.parse(filter.dataset.counts);
      }
      this.filters.set(filter.name, select);
    });

    // on change
    this.form.addEventListener('change', (event) => {
      this.section.classList.add('loading');

      const target = event.target as HTMLSelectElement;
      if (this.hasAdvancedFilters && target.name === this.filterSetup.primary) {
        const secondary = this.filters.get(
          this.filterSetup.secondary as string
        );

        if (!target.value) {
          const choices = secondary?.options;
          secondary?.select.setChoices(choices, 'value', 'label', true);
        } else {
          const choices = secondary?.counts?.[target.value];
          secondary?.select.setChoices(choices, 'value', 'label', true);
        }
      }

      const categoryContents =
        this.section.querySelectorAll('.category-content');
      if (categoryContents.length) {
        if (!target.value) {
          // show hide category text
          categoryContents?.forEach((content) => {
            content.classList.contains('category-content-default')
              ? content.classList.remove('hidden')
              : content.classList.add('hidden');
          });
        } else {
          // show hide category text
          categoryContents?.forEach((content) => {
            content.classList.add('hidden');
          });
          this.section
            .querySelector(`#${target.name}-${target.value}`)
            ?.classList.remove('hidden');
        }
      }

      const url = new URL(this.form.action);
      url.search = this.searchParamsFromData().toString();
      const href = url.href;
      this.fetchPosts(url.searchParams).then((content) => {
        if (content) {
          this.loadListing(content);

          history.pushState(
            { search: Object.fromEntries(url.searchParams.entries()) },
            '',
            href
          );
        }
      });
    });
  }

  setupPagination() {
    this.pagination = this.section.querySelector('.pagination') as HTMLElement;
    if (this.pagination) {
      const pager = this.pagination.querySelectorAll('a.page-link');
      pager?.forEach((link) =>
        link.addEventListener('click', (event: Event) => {
          event.preventDefault();
          this.section.classList.add('loading');

          const link = event.currentTarget as HTMLAnchorElement;
          const url = new URL(link.href);
          const href = url.href;
          this.fetchPosts(url.searchParams).then((content) => {
            if (content) {
              this.loadListing(content);

              history.pushState(
                { search: Object.fromEntries(url.searchParams.entries()) },
                '',
                href
              );
            }
          });
        })
      );
    }
  }

  loadListing(content: string): void {
    this.listing.innerHTML = content;

    const images = this.listing.querySelectorAll('img');
    if (!images.length) {
      this.section.classList.remove('loading');
    }

    let count = images.length;
    const loadImages = (e: Event) => {
      count--;
      if (count === 0) {
        // need wait 2 frames for html to settle ?!
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.section.classList.remove('loading');
          });
        });
      }
    };
    images?.forEach((image) => {
      image.addEventListener('load', loadImages);
      image.addEventListener('error', loadImages);
    });

    this.setupPagination();
  }

  searchParamsFromData(): URLSearchParams {
    const searchParams = new URLSearchParams();
    const formData = this.form ? new FormData(this.form) : new FormData();
    for (const [key, value] of formData.entries()) {
      if (typeof value === 'string' && value.trim() !== '') {
        searchParams.append(key, value);
      }
      // Add other checks if formData can contain other types
    }

    return searchParams;
  }

  async fetchPosts(data: URLSearchParams) {
    // merge in wrapper dataset
    Object.entries(this.data).forEach(([key, value]) => {
      key !== 'url' && data.append(key, value);
    });

    try {
      const response = await fetch(this.data.url, {
        method: 'POST', // *GET, POST, PUT, DELETE, etc.
        // mode: 'same-origin', // no-cors, *cors, same-origin
        // cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
        // credentials: 'same-origin', // include, *same-origin, omit
        // headers: {
        //     'Content-Type': 'application/x-www-form-urlencoded',
        // },
        // redirect: 'follow', // manual, *follow, error
        // referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        body: data, // body data type must match "Content-Type" header
      });
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return await response.text();
    } catch (e) {
      console.error(e);
    }
  }
}
